import { Grid } from '@material-ui/core';
import { _t } from 'app/utils/i18n';
import React, { FunctionComponent, memo, useRef, useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import TweetColumn from 'trends/components/TweetColumn';
import TweetReportPopup from '../TweetReportPopup/TweetReportPopup';
import { useMutation } from 'react-query';
import ErrorBoundary from '../ErrorBoundary';
import { getModuleName } from 'influencers/utils/getModuleName';
import { useLocation } from 'react-router-dom';
import { useReport } from 'reports/pages/AddNewReport/AddNewReport';
import { renderTweets } from 'reports/pages/AddTemplate/constants';
import { TInfIds } from 'app/utils/types';

interface Props {
  tweetColumnFilters: any;
  customFilters: any;
  analyzed?: boolean;
  slugName?: string;
  action: (arg: {
    body: any;
    isInfluencerModule?: boolean;
  }) => Promise<unknown>;
  hideSort?: boolean;
  isInfluencerModule?: boolean;
  keyExtractor?: string;
  isKeyValueArr?: boolean;
  hashtagDetails?: any;
  showTweetPopup?: boolean;
  endMessage?: string;
  reftechSentmentHandler?: any;
  setFilterIsLoading?: any;
  filterIsLoading?: boolean;
  compId: string;
  isReport?: boolean;
  isTemplate?: boolean;
  data?: any;
  openPopForTest?: any;
  reportState?: any;
  items?: any;
  isMultiTweets?: boolean;
  infIds?: TInfIds;
  filters?: any;
  influencerId?: number;
  handleInfAutoComplete?: { customFilters: any };
  tabName?: string;
  linkInSlug?: string;
  instagramSlug?: string;
  tikTokSlug?: string;
  TweetsRepliesSlug?: string;
  TweetsQuotesSlug?: string;
  setColsKey?: any;
  without_translate?: boolean;
  stopScroll?: boolean;
  showRegrab?: boolean;
  showAllColumn?: boolean;
  hideSliderFormPostCard?: boolean;
  showFavorite?: boolean;
  isPostTwitter?: boolean;
  hashtagsAutoCompleteEndPoint?: string;
  postDetailsUrl?: string;
}

const DraggableTweetsColumns: FunctionComponent<Props> = ({
  tweetColumnFilters,
  customFilters,
  analyzed,
  slugName,
  action,
  hideSort,
  isInfluencerModule,
  keyExtractor = '',
  isKeyValueArr,
  hashtagDetails,
  showTweetPopup,
  endMessage,
  reftechSentmentHandler,
  setFilterIsLoading,
  filterIsLoading,
  compId,
  isReport,
  isTemplate,
  data,
  openPopForTest,
  reportState,
  items,
  isMultiTweets,
  infIds,
  filters,
  influencerId,
  handleInfAutoComplete,
  tabName,
  linkInSlug,
  instagramSlug,
  tikTokSlug,
  setColsKey,
  without_translate,
  TweetsQuotesSlug,
  TweetsRepliesSlug,
  stopScroll,
  showRegrab,
  showAllColumn,
  hashtagsAutoCompleteEndPoint,
  hideSliderFormPostCard,
  showFavorite,
  postDetailsUrl,
  isPostTwitter,
}) => {
  const [isOpen, setIsOpen] = useState(openPopForTest ? true : false);
  const [tweetColumnItems, setTweetColumnItems] = useState([]);
  const [tweetContent, setTweetContent] = useState([]);
  const handleOpenPopup = () => setIsOpen(true);
  const handleClosePopup = () => setIsOpen(false);
  const { mutate, isLoading } = useMutation(action, {
    onSuccess: () => reftechSentmentHandler?.(),
  });
  const propsRef = useRef<any>({});
  const { pathname } = useLocation();
  const moduleName = getModuleName(pathname);
  const getTweetContent = (draggableId: string) => {
    const tweetItem: any = tweetColumnItems.filter(
      (item: any) => item.id == draggableId,
    );
    setTweetContent(tweetItem);
  };
  const onDrag = async ({ destination, draggableId }: any) => {
    if (analyzed) {
      // if (!destination?.droppableId) {
      //   toast.error(_t('basic:invalid_area'));
      //   return;
      // }
      const data = {
        category: destination?.droppableId?.toUpperCase(),
        ...customFilters,
        [keyExtractor]: isKeyValueArr ? [+draggableId] : +draggableId,
      };
      if (isInfluencerModule !== undefined) {
        mutate({ body: data, isInfluencerModule });
      } else {
        mutate({ body: data });
      }
      // setIsLoading(false);
    } else {
      toast.error(
        _t(
          `basic:not_analyzed${
            isInfluencerModule
              ? '_influencer'
              : moduleName === 'entities'
                ? '_entity'
                : ''
          }`,
        ),
      );
    }
  };
  const report = useReport();

  const tweetsColumns = [
    {
      type: 'negative',
      requestUrl: slugName,
      hideSort,
      isReport,
      customFilters: tweetColumnFilters,
      id: `${compId}_${
        tabName ? tabName.toLocaleLowerCase() : 'tweets'
      }_column_negative`,
      data: data,
    },
    {
      type: 'neutral',
      requestUrl: slugName,
      hideSort,
      isReport,
      customFilters: tweetColumnFilters,
      id: `${compId}_${
        tabName ? tabName.toLocaleLowerCase() : 'tweets'
      }_column_neutral`,
      data: data,
    },
    {
      type: 'positive',
      requestUrl: slugName,
      hideSort,
      isReport,
      customFilters: tweetColumnFilters,
      id: `${compId}_${
        tabName ? tabName.toLocaleLowerCase() : 'tweets'
      }_column_positive`,
      data: data,
    },
    (isReport || showAllColumn) && {
      type: 'all',
      requestUrl: slugName,
      hideSort,
      isReport,
      customFilters: tweetColumnFilters,
      id: `${compId}_${
        tabName ? tabName.toLocaleLowerCase() : 'tweets'
      }_column_all`,
      data: data,
    },
  ].filter(Boolean);
  const renderColumns = (columns: any) => {
    if (isMultiTweets)
      return isTemplate
        ? renderTweets(
            items,
            isInfluencerModule || false,
            infIds ? infIds : ({} as TInfIds),
            filters,
          )
        : report?.renderTweets();

    if (isTemplate)
      return columns
        .filter((item: any) => items?.some((i: any) => i?.slug === item?.id))
        .map((item: any) => ({
          ...item,
          customFilters: {
            ...item?.customFilters,
            limit: items.filter((i: any) => i?.slug === item?.id)?.[0]
              ?.item_count,
            order_column: items.filter((i: any) => i?.slug === item?.id)?.[0]
              ?.item_sort_type?.sort_colum,
          },
        }));
    if (isReport)
      return columns
        .filter((item: any) =>
          report?.values?.charts?.some((i: any) => i?.slug === item?.id),
        )
        .map((item: any) => ({
          ...item,
          customFilters: {
            ...item?.customFilters,
            limit: report?.values?.charts?.filter(
              (i: any) => i?.slug === item?.id,
            )?.[0]?.item_count,
            order_column: report?.values?.charts.filter(
              (i: any) => i?.slug === item?.id,
            )?.[0]?.item_sort_type?.sort_colum,
            type_of_sort: report?.values?.charts.filter(
              (i: any) => i?.slug === item?.id,
            )?.[0]?.sort_type,
          },
        }));
    return columns;
  };

  const handleShowReportAndDrag = (props: any) => {
    if (!analyzed) {
      toast.error(
        _t(
          `basic:not_analyzed${
            isInfluencerModule
              ? '_influencer'
              : moduleName === 'entities'
                ? '_entity'
                : ''
          }`,
        ),
      );
      return;
    }
    propsRef.current = props?.destination?.droppableId
      ? props
      : { ...props, destination: { droppableId: props?.source?.droppableId } }; //in case no destination take source and make it the destination
    handleOpenPopup();
  };
  return (
    <Grid container spacing={4}>
      <DragDropContext
        data-testid="drag-drop-holder"
        onDragEnd={(props: any) => {
          showTweetPopup
            ? (handleShowReportAndDrag(props),
              getTweetContent(props?.draggableId))
            : onDrag(props);
        }}
      >
        {showTweetPopup && (
          <ErrorBoundary>
            <TweetReportPopup
              pageContent={hashtagDetails}
              isOpen={isOpen}
              handleClosePopup={handleClosePopup}
              handleOpenPopup={handleOpenPopup}
              handleDrag={() => onDrag(propsRef.current)}
              tweetContent={tweetContent}
              destination={propsRef?.current?.destination?.droppableId}
              influencerId={influencerId}
              handleInfAutoComplete={handleInfAutoComplete}
              tabName={tabName}
              setColsKey={setColsKey}
              customFilters={customFilters}
              hashtagsAutoCompleteEndPoint={hashtagsAutoCompleteEndPoint}
              isPostTwitter={isPostTwitter}
            />
          </ErrorBoundary>
        )}
        {renderColumns(tweetsColumns)?.map((column: any, i: number) => (
          <Grid item lg={4} md={12} xs={12} key={`${column.id}_${i}`}>
            <ErrorBoundary>
              <TweetColumn
                stopScroll={stopScroll}
                requestUrl={
                  column?.id?.includes('linkedin')
                    ? linkInSlug
                      ? linkInSlug
                      : column.requestUrl
                    : column?.id?.includes('insta')
                      ? instagramSlug
                        ? instagramSlug
                        : column.requestUrl
                      : column?.id?.includes('tiktok')
                        ? tikTokSlug
                          ? tikTokSlug
                          : column.requestUrl
                        : column?.id?.includes('replies')
                          ? TweetsRepliesSlug
                            ? TweetsRepliesSlug
                            : column.requestUrl
                          : column?.id?.includes('quotes')
                            ? TweetsQuotesSlug
                              ? TweetsQuotesSlug
                              : column.requestUrl
                            : column.requestUrl
                }
                type={column.type}
                endMessage={endMessage}
                hideSort={column.hideSort}
                isReport={column.isReport}
                isTemplate={isTemplate}
                customFilters={column.customFilters}
                compId={column.id}
                setTweetsItems={setTweetColumnItems}
                isInfluencerModule={isInfluencerModule}
                setFilterIsLoading={setFilterIsLoading}
                filterIsLoading={filterIsLoading}
                showWrittenAt={compId?.includes('news')}
                data={column.data}
                reportState={reportState}
                chartId={column?.chart_id}
                tabName={column?.tabName ? column?.tabName : tabName}
                without_translate={without_translate}
                showRegrab={showRegrab}
                hideSliderFormPostCard={hideSliderFormPostCard}
                showFavorite={showFavorite}
                postDetailsUrl={postDetailsUrl}
              />
            </ErrorBoundary>
          </Grid>
        ))}
      </DragDropContext>
    </Grid>
  );
};
const MemoizedDraggableTweetsColumns = memo(DraggableTweetsColumns);
export default memo(MemoizedDraggableTweetsColumns);
